import React, { useEffect, useState, useCallback } from "react";
import * as dateFnsTz from "date-fns-tz";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  arrayUnion,
  updateDoc,
  where,
} from "firebase/firestore";
import { runTransaction } from "firebase/firestore";
import { db } from "../utils/firebase";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { Spinner } from "../components/index.js";
import {
  ArrowDownUp,
  Users,
  DollarSign,
  X,
  Download,
  FileText,
  History,
  Plus,
} from "lucide-react";
import { useAuth } from "../hooks/AuthenticationProvider";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const PaymentDocumentsModal = ({
  isOpen,
  onClose,
  documentDetails,
  setDocumentDetails,
  residents,
  onCreateDocument,
}) => {
  if (!isOpen) return null;

  const handleAddItem = () => {
    setDocumentDetails((prev) => ({
      ...prev,
      items: [...prev.items, { description: "", amount: "" }],
    }));
  };

  const handleRemoveItem = (index) => {
    setDocumentDetails((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index, field, value) => {
    setDocumentDetails((prev) => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, items: newItems };
    });
  };

  const calculateTotal = () => {
    let total = 0;
    documentDetails.items.forEach((item) => {
      const amount = parseFloat(item.amount);
      if (!isNaN(amount)) {
        total += amount;
      }
    });

    return total.toFixed(2);
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] touch-none"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
        <div
          className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between sticky top-0 bg-slate-900 z-10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <FileText className="text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white">
                {documentDetails.documentType === "receipt"
                  ? "Create Receipt"
                  : "Create Invoice"}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="text-slate-400 hover:text-slate-300" size={20} />
            </button>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              onCreateDocument();
            }}
            className="p-6 space-y-6"
          >
            <div className="space-y-4">
              {/* Document Type Selection */}
              <div className="flex gap-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="documentType"
                    value="receipt"
                    checked={documentDetails.documentType === "receipt"}
                    onChange={() =>
                      setDocumentDetails((prev) => ({
                        ...prev,
                        documentType: "receipt",
                      }))
                    }
                    className="mr-2"
                  />
                  <span className="text-white">Payment Receipt</span>
                </label>

                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="documentType"
                    value="invoice"
                    checked={documentDetails.documentType === "invoice"}
                    onChange={() =>
                      setDocumentDetails((prev) => ({
                        ...prev,
                        documentType: "invoice",
                      }))
                    }
                    className="mr-2"
                  />
                  <span className="text-white">Payment Invoice</span>
                </label>
              </div>

              {/* Resident Selection */}
              <div className="relative">
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Resident
                </label>
                <select
                  value={documentDetails.residentId}
                  onChange={(e) =>
                    setDocumentDetails((prev) => ({
                      ...prev,
                      residentId: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                >
                  <option value="">Select a resident</option>
                  {residents.map((resident) => (
                    <option key={resident.id} value={resident.id}>
                      {resident.firstName} {resident.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Due Date */}
              <div className="relative">
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={documentDetails.dueDate}
                  onChange={(e) =>
                    setDocumentDetails((prev) => ({
                      ...prev,
                      dueDate: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Line Items */}
              <div className="space-y-4">
                <label className="block text-sm font-semibold text-slate-300">
                  Items
                </label>
                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={documentDetails.startDate}
                      onChange={(e) =>
                        setDocumentDetails((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={documentDetails.endDate}
                      onChange={(e) =>
                        setDocumentDetails((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {documentDetails.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(index, "description", e.target.value)
                        }
                        placeholder="Description"
                        className="flex-grow px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={item.amount}
                        onChange={(e) =>
                          handleItemChange(index, "amount", e.target.value)
                        }
                        placeholder="Amount"
                        className="w-32 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      {documentDetails.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors w-full"
                  >
                    + Add Item
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div className="relative">
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Notes
                </label>
                <textarea
                  value={documentDetails.notes}
                  onChange={(e) =>
                    setDocumentDetails((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  className="w-full h-24 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Additional notes or payment instructions"
                />
              </div>

              {/* Total Display */}
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="flex justify-between items-center">
                  <span className="text-slate-300 font-medium">
                    Total Amount:
                  </span>
                  <span className="text-emerald-400 font-bold text-xl">
                    ${calculateTotal()}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-8">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-700 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-all shadow-lg"
              >
                {documentDetails.documentType === "receipt"
                  ? "Generate Receipt"
                  : "Generate Invoice"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

const formatDate = (inputDate) => {
  if (!inputDate) return "";

  let date;
  if (inputDate instanceof Timestamp) {
    date = inputDate.toDate();
  } else if (typeof inputDate === "string") {
    date = new Date(inputDate);
  } else if (inputDate instanceof Date) {
    date = inputDate;
  } else {
    console.warn("Invalid date format:", inputDate);
    return "";
  }

  if (isNaN(date.getTime())) {
    console.warn("Invalid date:", inputDate);
    return "";
  }

  // Get the user's local timezone, e.g., "America/New_York"
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Use formatInTimeZone to format the date in the user's timezone.
  // This returns a string formatted as 'yyyy-MM-dd'.
  return dateFnsTz.formatInTimeZone(date, userTimeZone, "yyyy-MM-dd");
};

const PaymentModal = ({
  isOpen,
  onClose,
  paymentDetails,
  handlePaymentDetailChange,
  handleSubmit,
  fundingSourceOptions = [],
  handleDeleteFundingSource,
  generateReceipt,
  setGenerateReceipt,
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] touch-none"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 touch-auto overscroll-none">
        <div
          className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto overscroll-contain"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900 z-[102]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <DollarSign className="text-emerald-400" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white tracking-tight">
                  Add Payment
                </h3>
                {paymentDetails.type && (
                  <p className="text-sm text-slate-400">
                    Type: {paymentDetails.type}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="text-slate-400 hover:text-slate-300" size={20} />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="space-y-4">
              <div className="relative">
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Payment Type
                </label>
                <select
                  value={paymentDetails.type}
                  onChange={(e) =>
                    handlePaymentDetailChange("type", e.target.value)
                  }
                  className="relative z-[103] w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all touch-auto"
                  required
                >
                  <option value="Cash">Cash</option>
                  <option value="Invoice">Invoice</option>
                  <option value="Check">Check</option>
                  <option value="ACH">ACH</option>
                  <option value="Venmo">Venmo</option>
                  <option value="Cash App">Cash App</option>
                </select>
              </div>

              <div className="relative">
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Payment Date
                </label>
                <input
                  name="paymentDate"
                  type="date"
                  value={
                    paymentDetails.paymentDate
                      ? paymentDetails.paymentDate.split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    handlePaymentDetailChange("paymentDate", e.target.value)
                  }
                  className="relative z-[103] w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all touch-auto"
                  required
                />
              </div>

              {paymentDetails.type !== "Invoice" ? (
                <>
                  <div className="relative">
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      Payment Amount
                    </label>
                    <input
                      name="paymentAmount"
                      type="number"
                      value={paymentDetails.amount}
                      onChange={(e) =>
                        handlePaymentDetailChange("amount", e.target.value)
                      }
                      className="relative z-[103] w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all touch-auto"
                      required
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      Payment Note
                    </label>
                    <textarea
                      value={paymentDetails.note}
                      onChange={(e) =>
                        handlePaymentDetailChange("note", e.target.value)
                      }
                      className="relative z-[103] w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all h-24 touch-auto"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="relative">
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      Funding Source
                    </label>
                    <div className="relative">
                      <select
                        name="fundingSource"
                        value={paymentDetails.fundingSource}
                        onChange={(e) =>
                          handlePaymentDetailChange(
                            "fundingSource",
                            e.target.value
                          )
                        }
                        className="relative z-[103] w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all touch-auto"
                      >
                        <option value="">Select funding source</option>
                        {(fundingSourceOptions || []).map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                        <option value="custom">+ Add new funding source</option>
                      </select>
                      {paymentDetails.fundingSource &&
                        paymentDetails.fundingSource !== "custom" && (
                          <div className="absolute right-10 top-1/2 -translate-y-1/2 z-[104]">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleDeleteFundingSource(
                                  paymentDetails.fundingSource
                                );
                              }}
                              className="p-1 hover:bg-red-500/20 rounded-full group"
                            >
                              <X className="w-4 h-4 text-red-400 group-hover:text-red-500" />
                            </button>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      Invoice Number
                    </label>
                    <input
                      name="invoiceNumber"
                      type="text"
                      value={paymentDetails.invoiceNumber}
                      onChange={(e) =>
                        handlePaymentDetailChange(
                          "invoiceNumber",
                          e.target.value
                        )
                      }
                      className="relative z-[103] w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all touch-auto"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      Invoice Week
                    </label>
                    <input
                      name="invoiceWeek"
                      type="text"
                      value={paymentDetails.invoiceWeek}
                      onChange={(e) =>
                        handlePaymentDetailChange("invoiceWeek", e.target.value)
                      }
                      className="relative z-[103] w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all touch-auto"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      Payment Amount
                    </label>
                    <input
                      name="paymentAmount"
                      type="number"
                      value={paymentDetails.amount}
                      onChange={(e) =>
                        handlePaymentDetailChange("amount", e.target.value)
                      }
                      className="relative z-[103] w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all touch-auto"
                      required
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      Payment Note
                    </label>
                    <textarea
                      value={paymentDetails.note}
                      onChange={(e) =>
                        handlePaymentDetailChange("note", e.target.value)
                      }
                      className="relative z-[103] w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all h-24 touch-auto"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center space-x-2 p-3 bg-slate-800/50 rounded-lg border border-slate-700 mt-6">
              <input
                type="checkbox"
                id="generateReceipt"
                checked={generateReceipt}
                onChange={(e) => setGenerateReceipt(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <label htmlFor="generateReceipt" className="text-white">
                Generate a receipt with this payment
              </label>
            </div>
            <div className="flex justify-end gap-4 mt-8">
              <button
                type="button"
                onClick={onClose}
                className="relative z-[103] px-6 py-2 text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-700 transition-all touch-auto"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="relative z-[103] px-6 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-all shadow-lg touch-auto"
              >
                Add Payment
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

const BalanceModal = ({ isOpen, onClose, currentBalance, onSubmit }) => {
  const [balanceDetails, setBalanceDetails] = useState({
    balance: currentBalance || 0,
    note: "",
  });

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] touch-none"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
        <div
          className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl w-full max-w-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <DollarSign className="text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white">Update Balance</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="text-slate-400 hover:text-slate-300" size={20} />
            </button>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit(balanceDetails);
            }}
            className="p-6 space-y-6"
          >
            <div className="space-y-4">
              <div className="relative">
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  New Balance
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={balanceDetails.balance}
                  onChange={(e) =>
                    setBalanceDetails((prev) => ({
                      ...prev,
                      balance: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Note
                </label>
                <textarea
                  value={balanceDetails.note}
                  onChange={(e) =>
                    setBalanceDetails((prev) => ({
                      ...prev,
                      note: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 h-24"
                />
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg"
              >
                Update Balance
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

const PaymentHistoryModal = ({
  isOpen,
  onClose,
  paymentHistoryData,
  onDownloadReceipt,
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] touch-none"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 touch-auto overscroll-none">
        <div
          className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] my-auto overflow-y-auto overscroll-contain"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 px-6 py-4 border-b border-slate-700 flex items-center justify-between bg-slate-900/95 backdrop-blur-sm z-[102]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <DollarSign className="text-emerald-400" size={20} />
              </div>
              <h3 className="text-xl font-semibold text-white">
                Payment History
              </h3>
            </div>
            <button
              onClick={onClose}
              className="relative z-[103] p-2 hover:bg-slate-800 rounded-lg transition-colors touch-auto"
            >
              <X className="text-slate-400 hover:text-slate-300" size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800 overscroll-contain">
            <div className="space-y-4">
              {/* Header Row */}
              <div className="grid grid-cols-5 gap-4 font-medium pb-2 sticky top-0 bg-slate-900/95 backdrop-blur-sm z-[102] p-2 rounded-lg">
                <p className="text-center text-slate-300">Date</p>
                <p className="text-center text-slate-300">Type</p>
                <p className="text-center text-slate-300">Amount</p>
                <p className="text-center text-slate-300">Balance</p>
                <p className="text-center text-slate-300">Receipt</p>
              </div>

              {/* Payment Rows */}
              {paymentHistoryData.map((payment) => (
                <details key={payment.id} className="group relative z-[103]">
                  <summary className="grid grid-cols-5 gap-4 items-center cursor-pointer p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors touch-auto">
                    <p className="text-center text-slate-300">
                      {payment.paymentDate.toDate().toLocaleDateString()}
                    </p>
                    <p className="text-center text-slate-300">
                      {payment.paymentType || "Cash"}
                    </p>
                    <p className="text-center text-emerald-400 font-medium">
                      ${(payment.paymentAmount || 0).toFixed(2)}
                    </p>
                    <p
                      className={`text-center font-medium ${
                        (payment.newBalance || 0) < 0
                          ? "text-red-400"
                          : "text-emerald-400"
                      }`}
                    >
                      ${(Number(payment.newBalance) || 0).toFixed(2)}
                    </p>
                    <div className="flex justify-center">
                      <button
                        onClick={(e) => {
                          e.preventDefault(); // Prevent the details from toggling
                          onDownloadReceipt(payment);
                        }}
                        className="bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-400 p-1.5 rounded-lg transition-all"
                        title="Download Receipt"
                      >
                        <Download size={16} />
                      </button>
                    </div>
                  </summary>
                  <div className="mt-2 p-4 bg-slate-800/30 rounded-lg border border-slate-700 space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Resident Name */}
                      {payment.residentName && (
                        <div className="space-y-1">
                          <p className="text-sm text-slate-400">Resident</p>
                          <p className="text-slate-300">
                            {payment.residentName}
                          </p>
                        </div>
                      )}

                      {/* Payment Note */}
                      {payment.paymentNote && (
                        <div className="space-y-1">
                          <p className="text-sm text-slate-400">Payment Note</p>
                          <p className="text-slate-300">
                            {payment.paymentNote}
                          </p>
                        </div>
                      )}

                      {/* Invoice Week */}
                      {payment.invoiceWeek && (
                        <div className="space-y-1">
                          <p className="text-sm text-slate-400">Invoice Week</p>
                          <p className="text-slate-300">
                            {payment.invoiceWeek}
                          </p>
                        </div>
                      )}

                      {/* Invoice Number - Show for all invoice type payments */}
                      {payment.paymentType === "Invoice" &&
                        payment.invoiceNumber && (
                          <div className="space-y-1">
                            <p className="text-sm text-slate-400">
                              Invoice Number
                            </p>
                            <p className="text-slate-300">
                              {payment.invoiceNumber}
                            </p>
                          </div>
                        )}

                      {/* Funding Source - Show for all payments that have it */}
                      {payment.fundingSource && (
                        <div className="space-y-1">
                          <p className="text-sm text-slate-400">
                            Funding Source
                          </p>
                          <p className="text-slate-300">
                            {payment.fundingSource}
                          </p>
                        </div>
                      )}

                      {/* Previous Balance */}
                      {typeof payment.previousBalance !== "undefined" && (
                        <div className="space-y-1">
                          <p className="text-sm text-slate-400">
                            Previous Balance
                          </p>
                          <p
                            className={`font-medium ${
                              payment.previousBalance < 0
                                ? "text-red-400"
                                : "text-emerald-400"
                            }`}
                          >
                            ${(Number(payment.previousBalance) || 0).toFixed(2)}
                          </p>
                        </div>
                      )}

                      {/* Payment Status */}
                      {typeof payment.paidInvoice === "boolean" && (
                        <div className="space-y-1">
                          <p className="text-sm text-slate-400">
                            Payment Status
                          </p>
                          <p
                            className={`font-medium ${
                              payment.paidInvoice
                                ? "text-emerald-400"
                                : "text-yellow-400"
                            }`}
                          >
                            {payment.paidInvoice ? "Paid" : "Pending"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Payments = () => {
  const { user, isLoading, organizationId, organizationData, checkPermission } =
    useAuth();

  const navigate = useNavigate();
  const [processedResidents, setProcessedResidents] = useState([]);
  const [fundingSourceOptions, setFundingSourceOptions] = useState([]);
  const [isNewFundingSourceModalOpen, setIsNewFundingSourceModalOpen] =
    useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [newFundingSource, setNewFundingSource] = useState("");
  const [showCreateReceiptModal, setShowCreateReceiptModal] = useState(false);
  const [receiptDetails, setReceiptDetails] = useState({
    residentId: "",
    items: [{ description: "Housing Fee", amount: "" }],
    dueDate: new Date().toISOString().split("T")[0],
    startDate: new Date().toISOString().split("T")[0], // Add this line
    endDate: new Date().toISOString().split("T")[0], // Add this line
    includeServiceFee: false,
    applyToBalance: false,
    notes: "",
    documentType: "receipt",
  });
  const [generateReceipt, setGenerateReceipt] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    amount: "",
    type: "Cash",
    paymentDate: new Date().toISOString().split("T")[0],
    note: "",
    fundingSource: "",
    invoiceNumber: "",
    invoiceWeek: "",
  });
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [residentId, setResidentId] = useState("");
  const [paymentHistoryModalOpen, setPaymentHistoryModalOpen] = useState(false);
  const [paymentHistoryData, setPaymentHistoryData] = useState([]);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);

  const residents = organizationData?.residents || [];

  useEffect(() => {
    const loadFundingSources = async () => {
      if (!organizationId) return;

      try {
        const settingsRef = doc(
          db,
          `organizations/${organizationId}/houseSettings/initial`
        );
        const settingsSnap = await getDoc(settingsRef);

        if (settingsSnap.exists()) {
          // Check if fundingSources exists and is an array
          const data = settingsSnap.data();
          if (data && Array.isArray(data.fundingSources)) {
            setFundingSourceOptions(data.fundingSources);
          } else {
            // Initialize with empty array if fundingSources doesn't exist or isn't an array
            await updateDoc(settingsRef, {
              fundingSources: [],
            });
            setFundingSourceOptions([]);
          }
        } else {
          // Create the document if it doesn't exist
          await setDoc(settingsRef, {
            fundingSources: [],
          });
          setFundingSourceOptions([]);
        }
      } catch (error) {
        console.error("Error loading funding sources:", error);
        setFundingSourceOptions([]);
      }
    };

    loadFundingSources();
  }, [organizationId]);

  useEffect(() => {
    if (!residents) return;

    const processedData = residents
      .filter((resident) => resident.currentResident)
      .map((resident) => ({
        id: resident.id,
        ...resident,
        rentSchedule: resident.rentSchedule?.toLowerCase(),
      }))
      .sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(
          `${b.firstName} ${b.lastName}`
        )
      );

    setProcessedResidents(processedData);
  }, [residents]);

  const handleAddFundingSource = async (newSource) => {
    if (!newSource || !organizationId) return;

    try {
      const settingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );

      // Get current funding sources first
      const settingsSnap = await getDoc(settingsRef);
      const currentSources = settingsSnap.exists()
        ? settingsSnap.data().fundingSources || []
        : [];

      // Check if source already exists
      if (currentSources.includes(newSource)) {
        console.log("Funding source already exists");
        return;
      }

      // Add new source using arrayUnion
      await updateDoc(settingsRef, {
        fundingSources: arrayUnion(newSource),
      });

      // Update local state
      setFundingSourceOptions((prev) => [...prev, newSource]);

      // Update payment details if needed
      setPaymentDetails((prev) => ({
        ...prev,
        fundingSource: newSource,
      }));
    } catch (error) {
      console.error("Error adding funding source:", error);
    }
  };

  const handleDeleteFundingSource = async (sourceToDelete) => {
    if (
      window.confirm(
        "Are you sure you want to remove this funding source from the list?"
      )
    ) {
      try {
        const settingsRef = doc(
          db,
          `organizations/${organizationId}/houseSettings/initial`
        );

        // Get current array and filter out the source to delete
        const updatedSources = fundingSourceOptions.filter(
          (source) => source !== sourceToDelete
        );

        // Update Firestore
        await updateDoc(settingsRef, {
          fundingSources: updatedSources,
        });

        // Update local state
        setFundingSourceOptions(updatedSources);

        // If the deleted source was selected, clear the selection
        if (paymentDetails.fundingSource === sourceToDelete) {
          setPaymentDetails((prev) => ({
            ...prev,
            fundingSource: "",
          }));
        }
      } catch (error) {
        console.error("Error removing funding source:", error);
      }
    }
  };

  const handleDownloadReceipt = (payment) => {
    // Find the resident associated with this payment
    const resident = processedResidents.find(
      (r) => r.id === payment.residentId
    );
    if (!resident) {
      console.error("Resident not found for payment:", payment);
      return;
    }

    // Pass organizationData as the third parameter
    const doc = generateReceiptPDF(payment, resident, organizationData);

    // Generate a filename
    const filename = `receipt_${resident.lastName}_${new Date(
      payment.paymentDate.toDate()
    )
      .toISOString()
      .slice(0, 10)}.pdf`;

    // Save the PDF
    doc.save(filename);
  };

  // Function to generate an invoice PDF
  const generateInvoicePDF = (invoiceData, resident, organizationData = {}) => {
    const doc = new jsPDF();

    // Check if we have organization header data
    if (
      organizationData?.houseSettings?.headerLogo ||
      organizationData?.houseSettings?.headerTitle ||
      organizationData?.houseSettings?.headerContact
    ) {
      let yPosition = 10;

      // Add logo if exists with full width
      if (organizationData.houseSettings.headerLogo) {
        try {
          // Calculate the width of the page (with margins)
          const pageWidth = doc.internal.pageSize.width - 28; // 14 units margin on each side
          const logoHeight = 30; // Adjusted height for better appearance

          doc.addImage(
            organizationData.houseSettings.headerLogo,
            "JPEG",
            14,
            yPosition,
            pageWidth,
            logoHeight
          );
          yPosition += logoHeight + 8; // Add spacing after logo
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      }

      // Add organization name if exists
      if (organizationData.houseSettings.headerTitle) {
        doc.setFontSize(18); // Larger font for title
        doc.setFont(undefined, "bold");
        doc.text(organizationData.houseSettings.headerTitle, 14, yPosition);
        yPosition += 10;
      }

      // Add contact info if exists
      if (organizationData.houseSettings.headerContact) {
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        const contactLines =
          organizationData.houseSettings.headerContact.split("\n");
        contactLines.forEach((line) => {
          doc.text(line, 14, yPosition);
          yPosition += 5;
        });
        yPosition += 5;
      }

      // Add divider line
      doc.setLineWidth(0.5);
      doc.line(14, yPosition, doc.internal.pageSize.width - 14, yPosition);
      yPosition += 15; // Add more space after divider

      // Invoice title
      doc.setFontSize(20);
      doc.setFont(undefined, "bold");
      doc.text("INVOICE", doc.internal.pageSize.width / 2, yPosition, {
        align: "center",
      });
      yPosition += 15;

      // Invoice details
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(
        `Invoice #: ${invoiceData.invoiceNumber || new Date().getTime()}`,
        14,
        yPosition
      );
      yPosition += 5;
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, yPosition);
      yPosition += 5;
      doc.text(
        `Due Date: ${new Date(invoiceData.dueDate).toLocaleDateString()}`,
        14,
        yPosition
      );
      yPosition += 10;

      if (invoiceData.startDate && invoiceData.endDate) {
        yPosition += 5;
        doc.text(
          `Service Period: ${new Date(
            invoiceData.startDate
          ).toLocaleDateString()} - ${new Date(
            invoiceData.endDate
          ).toLocaleDateString()}`,
          14,
          yPosition
        );
        yPosition += 10; // Add this line to create more space after the service period
      }

      // Resident Information
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Bill To:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");
      doc.text(`${resident.firstName} ${resident.lastName}`, 14, yPosition);
      yPosition += 5;
      if (resident.address) {
        doc.text(resident.address, 14, yPosition);
        yPosition += 5;
      }
      yPosition += 10;

      // Invoice items
      const tableColumn = ["Description", "Amount"];
      const tableRows = [];

      // Add all invoice items
      let subtotal = 0;
      invoiceData.items.forEach((item) => {
        const amount = parseFloat(item.amount);
        if (!isNaN(amount)) {
          subtotal += amount;
          tableRows.push([item.description, `$${amount.toFixed(2)}`]);
        }
      });

      // Add service fee if included
      if (invoiceData.includeServiceFee) {
        const serviceFee = subtotal * 0.03; // 3% service fee
        tableRows.push(["Service Fee (3%)", `$${serviceFee.toFixed(2)}`]);
        subtotal += serviceFee;
      }

      // Add the total row
      tableRows.push(["Total", `$${subtotal.toFixed(2)}`]);

      // Generate the table
      autoTable(doc, {
        startY: yPosition,
        head: [tableColumn],
        body: tableRows,
        theme: "grid",
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        foot: [
          [
            {
              content: "Total Due:",
              styles: { fontStyle: "bold", halign: "right" },
            },
            {
              content: `$${subtotal.toFixed(2)}`,
              styles: { fontStyle: "bold", halign: "right" },
            },
          ],
        ],
        footStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },
      });

      // Get the Y position after the table
      yPosition = doc.lastAutoTable.finalY + 20;

      // Add notes if any
      if (invoiceData.notes) {
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text("Notes:", 14, yPosition);
        yPosition += 7;
        doc.setFont(undefined, "normal");
        const noteLines = doc.splitTextToSize(invoiceData.notes, 180);
        doc.text(noteLines, 14, yPosition);
      }

      // Add payment terms at the bottom
      yPosition = doc.internal.pageSize.height - 40;
      doc.setFontSize(10);
      doc.setFont(undefined, "italic");
      doc.text(
        "Payment Terms: Due upon above due date. Please make payments to the house leader.",
        14,
        yPosition
      );
    } else {
      // Simple version without header
      doc.setFontSize(20);
      doc.setFont(undefined, "bold");
      doc.text("INVOICE", doc.internal.pageSize.width / 2, 20, {
        align: "center",
      });

      let yPosition = 40;

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(
        `Invoice #: ${invoiceData.invoiceNumber || new Date().getTime()}`,
        14,
        yPosition
      );
      yPosition += 5;
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, yPosition);
      yPosition += 5;
      doc.text(
        `Due Date: ${new Date(invoiceData.dueDate).toLocaleDateString()}`,
        14,
        yPosition
      );
      yPosition += 10;

      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Bill To:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");
      doc.text(`${resident.firstName} ${resident.lastName}`, 14, yPosition);
      yPosition += 10;

      const tableColumn = ["Description", "Amount"];
      const tableRows = [];

      let subtotal = 0;
      invoiceData.items.forEach((item) => {
        const amount = parseFloat(item.amount);
        if (!isNaN(amount)) {
          subtotal += amount;
          tableRows.push([item.description, `$${amount.toFixed(2)}`]);
        }
      });

      if (invoiceData.includeServiceFee) {
        const serviceFee = subtotal * 0.03;
        tableRows.push(["Service Fee (3%)", `$${serviceFee.toFixed(2)}`]);
        subtotal += serviceFee;
      }

      tableRows.push(["Total", `$${subtotal.toFixed(2)}`]);

      autoTable(doc, {
        startY: yPosition,
        head: [tableColumn],
        body: tableRows,
        theme: "grid",
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
      });

      yPosition = doc.lastAutoTable.finalY + 20;

      if (invoiceData.notes) {
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text("Notes:", 14, yPosition);
        yPosition += 7;
        doc.setFont(undefined, "normal");
        const noteLines = doc.splitTextToSize(invoiceData.notes, 180);
        doc.text(noteLines, 14, yPosition);
      }
    }

    return doc;
  };

  // Function to generate a receipt PDF
  const generateReceiptPDF = (payment, resident, organizationData = {}) => {
    const doc = new jsPDF();

    // Check if we have organization header data
    if (
      organizationData?.houseSettings?.headerLogo ||
      organizationData?.houseSettings?.headerTitle ||
      organizationData?.houseSettings?.headerContact
    ) {
      let yPosition = 10;

      // Add logo if exists with full width
      if (organizationData.houseSettings.headerLogo) {
        try {
          // Calculate the width of the page (with margins)
          const pageWidth = doc.internal.pageSize.width - 28; // 14 units margin on each side
          const logoHeight = 30; // Adjusted height for better appearance

          doc.addImage(
            organizationData.houseSettings.headerLogo,
            "JPEG",
            14,
            yPosition,
            pageWidth,
            logoHeight
          );
          yPosition += logoHeight + 8; // Add spacing after logo
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      }

      // Add organization name if exists
      if (organizationData.houseSettings.headerTitle) {
        doc.setFontSize(18); // Larger font for title
        doc.setFont(undefined, "bold");
        doc.text(organizationData.houseSettings.headerTitle, 14, yPosition);
        yPosition += 10;
      }

      // Add contact info if exists
      if (organizationData.houseSettings.headerContact) {
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        const contactLines =
          organizationData.houseSettings.headerContact.split("\n");
        contactLines.forEach((line) => {
          doc.text(line, 14, yPosition);
          yPosition += 5;
        });
        yPosition += 5;
      }

      // Add divider line
      doc.setLineWidth(0.5);
      doc.line(14, yPosition, doc.internal.pageSize.width - 14, yPosition);
      yPosition += 15; // Add more space after divider

      // Receipt title
      doc.setFontSize(20);
      doc.setFont(undefined, "bold");
      doc.text("PAYMENT RECEIPT", doc.internal.pageSize.width / 2, yPosition, {
        align: "center",
      });
      yPosition += 15;

      // Receipt details
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(
        `Receipt #: ${payment.id || new Date().getTime()}`,
        14,
        yPosition
      );
      yPosition += 5;
      doc.text(
        `Date: ${payment.paymentDate.toDate().toLocaleDateString()}`,
        14,
        yPosition
      );
      yPosition += 10;

      if (payment.startDate && payment.endDate) {
        yPosition += 5;
        doc.text(
          `Service Period: ${new Date(
            payment.startDate
          ).toLocaleDateString()} - ${new Date(
            payment.endDate
          ).toLocaleDateString()}`,
          14,
          yPosition
        );
        yPosition += 10;
      }

      // Resident Information
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Received From:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");
      doc.text(`${resident.firstName} ${resident.lastName}`, 14, yPosition);
      yPosition += 10;

      // Payment Information
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Payment Information:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");

      const paymentInfoTable = [
        ["Payment Type:", payment.paymentType || "Cash"],
        ["Amount:", `$${parseFloat(payment.paymentAmount).toFixed(2)}`],
        [
          "Previous Balance:",
          `$${parseFloat(payment.previousBalance).toFixed(2)}`,
        ],
        ["New Balance:", `$${parseFloat(payment.newBalance).toFixed(2)}`],
      ];

      // Add invoice number if exists
      if (payment.invoiceNumber) {
        paymentInfoTable.push(["Invoice Number:", payment.invoiceNumber]);
      }

      // Add invoice week if exists
      if (payment.invoiceWeek) {
        paymentInfoTable.push(["Invoice Week:", payment.invoiceWeek]);
      }

      // Add funding source if exists
      if (payment.fundingSource) {
        paymentInfoTable.push(["Funding Source:", payment.fundingSource]);
      }

      // Generate the table
      autoTable(doc, {
        startY: yPosition,
        body: paymentInfoTable,
        theme: "plain",
        styles: {
          cellPadding: 3,
        },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 40 },
          1: { cellWidth: "auto" },
        },
      });

      // Get the Y position after the table
      yPosition = doc.lastAutoTable.finalY + 20;

      // Add notes if any
      if (payment.paymentNote) {
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text("Notes:", 14, yPosition);
        yPosition += 7;
        doc.setFont(undefined, "normal");
        const noteLines = doc.splitTextToSize(payment.paymentNote, 180);
        doc.text(noteLines, 14, yPosition);
      }

      // Add thank you message at the bottom
      yPosition = doc.internal.pageSize.height - 40;
      doc.setFontSize(11);
      doc.setFont(undefined, "italic");
      doc.text(
        "Thank you for your payment!",
        doc.internal.pageSize.width / 2,
        yPosition,
        { align: "center" }
      );
    } else {
      // Simple version without header
      doc.setFontSize(20);
      doc.setFont(undefined, "bold");
      doc.text("PAYMENT RECEIPT", doc.internal.pageSize.width / 2, 20, {
        align: "center",
      });

      let yPosition = 40;

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(
        `Receipt #: ${payment.id || new Date().getTime()}`,
        14,
        yPosition
      );
      yPosition += 5;
      doc.text(
        `Date: ${payment.paymentDate.toDate().toLocaleDateString()}`,
        14,
        yPosition
      );
      yPosition += 10;

      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Received From:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");
      doc.text(`${resident.firstName} ${resident.lastName}`, 14, yPosition);
      yPosition += 10;

      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Payment Information:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");

      const paymentInfoTable = [
        ["Payment Type:", payment.paymentType || "Cash"],
        ["Amount:", `$${parseFloat(payment.paymentAmount).toFixed(2)}`],
        [
          "Previous Balance:",
          `$${parseFloat(payment.previousBalance).toFixed(2)}`,
        ],
        ["New Balance:", `$${parseFloat(payment.newBalance).toFixed(2)}`],
      ];

      if (payment.invoiceNumber) {
        paymentInfoTable.push(["Invoice Number:", payment.invoiceNumber]);
      }

      if (payment.invoiceWeek) {
        paymentInfoTable.push(["Invoice Week:", payment.invoiceWeek]);
      }

      if (payment.fundingSource) {
        paymentInfoTable.push(["Funding Source:", payment.fundingSource]);
      }

      autoTable(doc, {
        startY: yPosition,
        body: paymentInfoTable,
        theme: "plain",
        styles: {
          cellPadding: 3,
        },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 40 },
          1: { cellWidth: "auto" },
        },
      });

      yPosition = doc.lastAutoTable.finalY + 20;

      if (payment.paymentNote) {
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text("Notes:", 14, yPosition);
        yPosition += 7;
        doc.setFont(undefined, "normal");
        const noteLines = doc.splitTextToSize(payment.paymentNote, 180);
        doc.text(noteLines, 14, yPosition);
      }

      yPosition = doc.internal.pageSize.height - 40;
      doc.setFontSize(11);
      doc.setFont(undefined, "italic");
      doc.text(
        "Thank you for your payment!",
        doc.internal.pageSize.width / 2,
        yPosition,
        { align: "center" }
      );
    }

    return doc;
  };

  const handleCreateDocument = async () => {
    if (!receiptDetails.residentId) {
      alert("Please select a resident");
      return;
    }

    const resident = processedResidents.find(
      (r) => r.id === receiptDetails.residentId
    );
    if (!resident) {
      alert("Resident not found");
      return;
    }

    try {
      // Calculate the total amount from all items
      const totalAmount = receiptDetails.items.reduce(
        (total, item) => total + (parseFloat(item.amount) || 0),
        0
      );

      // Get the current balance
      const currentBalance = resident.balance || 0;

      // Calculate what the new balance would be (but we won't update it here anymore)
      const newBalance =
        receiptDetails.documentType === "receipt"
          ? currentBalance + totalAmount
          : currentBalance;

      // Generate the appropriate PDF based on document type
      let doc;
      try {
        if (receiptDetails.documentType === "invoice") {
          // Pass organizationData as the third parameter
          doc = generateInvoicePDF(receiptDetails, resident, organizationData);
        } else {
          // For receipt, create a payment-like object
          const receiptPayment = {
            id: `RCP-${Date.now()}`,
            paymentDate: Timestamp.now(),
            paymentType: "Receipt",
            paymentAmount: totalAmount,
            previousBalance: currentBalance,
            newBalance: currentBalance + totalAmount,
            paymentNote: receiptDetails.notes || "",
            residentId: resident.id,
            startDate: receiptDetails.startDate || null,
            endDate: receiptDetails.endDate || null,
          };

          // Pass organizationData as the third parameter
          doc = generateReceiptPDF(receiptPayment, resident, organizationData);
        }
      } catch (pdfError) {
        console.error("Error generating PDF:", pdfError);
        alert(`Error generating PDF: ${pdfError.message}`);
        return;
      }

      // Generate a filename
      const filename = `${receiptDetails.documentType}_${
        resident.lastName
      }_${new Date().toISOString().slice(0, 10)}.pdf`;

      try {
        // Save the PDF
        doc.save(filename);
      } catch (saveError) {
        console.error("Error saving PDF:", saveError);
        alert(`Error saving PDF: ${saveError.message}`);
        return;
      }

      // Store a record of the document in Firestore
      if (organizationId) {
        try {
          const docRef = await addDoc(
            collection(db, `organizations/${organizationId}/documents`),
            {
              type: receiptDetails.documentType,
              residentId: resident.id,
              residentName: `${resident.firstName} ${resident.lastName}`,
              amount: totalAmount,
              createdAt: Timestamp.now(),
              createdBy: user.displayName || user.email,
              dueDate: receiptDetails.dueDate
                ? Timestamp.fromDate(new Date(receiptDetails.dueDate))
                : null,
              startDate: receiptDetails.startDate
                ? Timestamp.fromDate(new Date(receiptDetails.startDate))
                : null,
              endDate: receiptDetails.endDate
                ? Timestamp.fromDate(new Date(receiptDetails.endDate))
                : null,
              items: receiptDetails.items,
              notes: receiptDetails.notes,
              includeServiceFee: receiptDetails.includeServiceFee,
            }
          );

          console.log(`Document created with ID: ${docRef.id}`);
        } catch (firestoreError) {
          console.error("Error storing document record:", firestoreError);
          // Don't return here - we still want to close the modal even if Firestore fails
        }
      }

      // Reset and close the modal
      setShowCreateReceiptModal(false);
      setReceiptDetails({
        residentId: "",
        items: [{ description: "Housing Fee", amount: "" }],
        dueDate: new Date().toISOString().split("T")[0],
        includeServiceFee: false,
        notes: "",
        documentType: "receipt",
      });
    } catch (error) {
      console.error("Error generating document:", error);
      alert(`Failed to generate document: ${error.message}`);
    }
  };

  const handleAddPaymentClick = useCallback((residentId) => {
    setResidentId(residentId); // Set the selected resident ID
    setShowAddPaymentModal(true); // Open the modal
  }, []);

  const handleDueDateChange = useCallback(
    async (residentId, value) => {
      if (!organizationId) {
        console.error("No organization ID found");
        return;
      }

      console.log(`Changing due date for resident ${residentId} to ${value}`);

      // Convert the input value (YYYY-MM-DD) to a Date object.
      // For organizations without a specific timezone on the client,
      // we force the time to 05:00 UTC as a fallback.
      const dateObj = value ? new Date(value) : null;
      if (dateObj) {
        dateObj.setUTCHours(5, 0, 0, 0);
      }

      // Create a Firestore Timestamp from the date
      const rentDueDateTimestamp = dateObj ? Timestamp.fromDate(dateObj) : null;

      // Update local state
      setProcessedResidents((currentResidents) =>
        currentResidents.map((resident) => {
          if (resident.id === residentId) {
            return {
              ...resident,
              rentDueDate: rentDueDateTimestamp,
            };
          }
          return resident;
        })
      );

      try {
        const residentDoc = doc(
          db,
          `organizations/${organizationId}/residents`,
          residentId
        );
        await updateDoc(residentDoc, {
          rentDueDate: rentDueDateTimestamp,
        });
        console.log(
          `Successfully updated rentDueDate for resident ${residentId} to ${value}`
        );
      } catch (error) {
        console.error(
          `Error updating rentDueDate for resident ${residentId}:`,
          error
        );
      }
    },
    [organizationId]
  );

  const handlePriceChange = useCallback(
    async (residentId, value) => {
      if (!organizationId) {
        console.error("No organization ID found");
        return;
      }

      const newValue = parseFloat(value);
      console.log(
        `Changing rent amount for resident ${residentId} to ${newValue}`
      );

      setProcessedResidents((currentResidents) =>
        currentResidents.map((resident) => {
          if (resident.id === residentId) {
            return {
              ...resident,
              rentAmount: newValue,
            };
          }
          return resident;
        })
      );

      try {
        const residentDoc = doc(
          db,
          `organizations/${organizationId}/residents`,
          residentId
        );
        await updateDoc(residentDoc, { rentAmount: newValue });
        console.log(
          `Successfully updated rentAmount for resident ${residentId} to ${newValue}`
        );
      } catch (error) {
        console.error(
          `Error updating rentAmount for resident ${residentId}:`,
          error
        );
      }
    },
    [organizationId]
  );

  const handlePeriodChange = useCallback(
    async (residentId, value) => {
      if (!organizationId) {
        console.error("No organization ID found");
        return;
      }

      const newValue = value.toLowerCase();
      console.log(`Changing period for resident ${residentId} to ${newValue}`);

      setProcessedResidents((currentResidents) =>
        currentResidents.map((resident) => {
          if (resident.id === residentId) {
            return {
              ...resident,
              rentSchedule: newValue,
            };
          }
          return resident;
        })
      );

      try {
        const residentDoc = doc(
          db,
          `organizations/${organizationId}/residents`,
          residentId
        );
        await updateDoc(residentDoc, { rentSchedule: newValue });
        console.log(
          `Successfully updated rentSchedule for resident ${residentId} to ${newValue}`
        );
      } catch (error) {
        console.error(
          `Error updating rentSchedule for resident ${residentId}:`,
          error
        );
      }
    },
    [organizationId]
  );

  const handleViewHistory = useCallback(
    async (residentId) => {
      if (!organizationId) {
        console.error("No organization ID found");
        return;
      }

      try {
        const paymentSnapshot = await getDocs(
          collection(
            db,
            `organizations/${organizationId}/residents/${residentId}/payments`
          )
        );

        const payments = paymentSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const sortedPayments = [...payments].sort(
          (a, b) => b.paymentDate.toDate() - a.paymentDate.toDate()
        );

        setPaymentHistoryData(sortedPayments);
        setPaymentHistoryModalOpen(true);
      } catch (error) {
        console.error("Error fetching payment history:", error);
      }
    },
    [organizationId]
  );

  const handleAddPaymentModalClose = useCallback(() => {
    setShowAddPaymentModal(false); // Close the modal
    setPaymentDetails({
      amount: "",
      type: "Cash",
      note: "",
      fundingSource: "",
      invoiceNumber: "",
      invoiceWeek: "",
    }); // Reset payment details
  }, []);

  // Handle adding a payment with dual-collection approach
  const handleAddPaymentSubmit = async (event) => {
    event.preventDefault();
    if (!organizationId) {
      setErrorMessage("Organization ID not found");
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }
    try {
      let updatedBalance; // Declare this outside to use after transaction
      let paymentId; // Store the payment ID for receipt generation
      let previousBalance; // Store the previous balance for receipt generation

      await runTransaction(db, async (transaction) => {
        const residentRef = doc(
          db,
          `organizations/${organizationId}/residents`,
          residentId
        );
        const residentSnap = await transaction.get(residentRef);
        if (!residentSnap.exists()) {
          throw new Error("Resident not found");
        }
        const residentData = residentSnap.data();
        const paymentAmountNum = Number(paymentDetails.amount) || 0;
        if (isNaN(paymentAmountNum)) {
          throw new Error("Invalid payment amount");
        }
        const currentBalance = Number(residentData.balance) || 0;
        previousBalance = currentBalance; // Save the previous balance for receipt generation
        updatedBalance = currentBalance + paymentAmountNum; // Save to outer scope

        // Create payment in resident's subcollection
        const paymentRef = doc(
          collection(
            db,
            `organizations/${organizationId}/residents/${residentId}/payments`
          )
        );
        paymentId = paymentRef.id; // Store the payment ID

        const newPayment = {
          paymentAmount: paymentAmountNum,
          paymentType: paymentDetails.type,
          paymentNote: paymentDetails.note,
          fundingSource: paymentDetails.fundingSource,
          invoiceNumber: paymentDetails.invoiceNumber,
          invoiceWeek: paymentDetails.invoiceWeek,
          paymentDate: Timestamp.now(),
          residentId,
          residentName: `${residentData.firstName} ${residentData.lastName}`,
          newBalance: updatedBalance,
          previousBalance: currentBalance,
          ...(paymentDetails.type === "Invoice" && { paidInvoice: false }),
        };

        transaction.set(paymentRef, newPayment);
        transaction.update(residentRef, {
          balance: updatedBalance,
          lastPaymentDate: Timestamp.now(),
        });

        // If it's an invoice, also add to the invoices collection
        if (paymentDetails.type === "Invoice") {
          // Create an invoice document with the SAME ID as the payment document
          const invoiceRef = doc(
            db,
            `organizations/${organizationId}/invoices`,
            paymentRef.id // Use the same ID for both documents
          );

          const invoiceData = {
            ...newPayment,
            originalPaymentRef: paymentRef.path, // Store reference to original document
            // Don't include an "id" field in the data, since Firestore already has its own ID
          };

          transaction.set(invoiceRef, invoiceData);
        }
      });

      // Now we can use updatedBalance here
      setProcessedResidents((current) =>
        current.map((resident) =>
          resident.id === residentId
            ? { ...resident, balance: updatedBalance }
            : resident
        )
      );

      // Generate receipt if checkbox is checked
      if (generateReceipt) {
        // Get the resident
        const resident = processedResidents.find((r) => r.id === residentId);
        if (resident) {
          const currentDate = new Date();
          // Create payment data for the receipt
          const receiptPayment = {
            id: paymentId || `P-${Date.now()}`,
            paymentDate: Timestamp.now(),
            paymentType: paymentDetails.type,
            paymentAmount: Number(paymentDetails.amount) || 0,
            // Use the previousBalance we captured earlier in the transaction
            previousBalance: previousBalance,
            newBalance: updatedBalance,
            paymentNote: paymentDetails.note || "",
            residentId: residentId,
            fundingSource: paymentDetails.fundingSource,
            invoiceNumber: paymentDetails.invoiceNumber,
            invoiceWeek: paymentDetails.invoiceWeek,
            // Add these lines for date range support
            startDate: paymentDetails.startDate || null,
            endDate: paymentDetails.endDate || null,
          };

          // Generate the receipt
          const doc = generateReceiptPDF(
            receiptPayment,
            resident,
            organizationData
          );

          // Save the PDF
          const filename = `receipt_${resident.lastName}_${currentDate
            .toISOString()
            .slice(0, 10)}.pdf`;
          doc.save(filename);
        }
      }

      handleAddPaymentModalClose();
    } catch (error) {
      console.error("Error adding payment:", error);
      setErrorMessage(error.message || "Failed to add payment");
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  };

  const handleBalanceUpdate = useCallback(
    async (details) => {
      try {
        await runTransaction(db, async (transaction) => {
          const residentRef = doc(
            db,
            `organizations/${organizationId}/residents`,
            selectedResident.id
          );
          const residentSnap = await transaction.get(residentRef);

          const oldBalance = Number(residentSnap.data().balance) || 0;
          const newBalance = Number(details.balance);

          transaction.update(residentRef, {
            balance: newBalance,
            lastUpdatedAt: Timestamp.now(),
          });

          const adjustmentRef = doc(
            collection(
              db,
              `organizations/${organizationId}/residents/${selectedResident.id}/payments`
            )
          );

          transaction.set(adjustmentRef, {
            paymentAmount: Number(newBalance - oldBalance) || 0,
            paymentType: "Balance Adjustment",
            paymentNote: details.note || "",
            paymentDate: Timestamp.now(),
            residentId: selectedResident.id,
            residentName: selectedResident.fullName || "",
            newBalance: Number(newBalance) || 0,
            previousBalance: Number(oldBalance) || 0,
            adjustedBy: user.displayName || user.email || "",
          });
        });

        setProcessedResidents((current) =>
          current.map((resident) =>
            resident.id === selectedResident.id
              ? { ...resident, balance: Number(details.balance) }
              : resident
          )
        );

        setShowBalanceModal(false);
      } catch (error) {
        setErrorMessage(error.message || "Failed to update balance");
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    },
    [organizationId, selectedResident, user]
  );

  const handlePaymentDetailChange = (field, value) => {
    if (field === "fundingSource" && value === "custom") {
      setIsNewFundingSourceModalOpen(true);
      return;
    }
    setPaymentDetails((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <>
      <Helmet>
        <title>&bull; Payments</title>
      </Helmet>

      {showError && (
        <div className="fixed left-1/2 top-8 -translate-x-1/2 z-50">
          <div className="bg-slate-800 text-white px-6 py-4 rounded-xl shadow-xl border border-red-500/20 flex items-center gap-3">
            <div className="bg-red-500/20 rounded-full p-2">
              <X className="text-red-400" size={20} />
            </div>
            <div>
              <p className="font-medium">Payment Failed</p>
              <p className="text-slate-400 text-sm">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-emerald-950/30 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Payment Management
              </h1>
            </div>
          </div>

          {/* Action buttons and filters */}
          <div className="grid grid-cols-1 gap-4 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              {checkPermission("viewInvoice") && (
                <button
                  onClick={() => (window.location.href = "payments/invoices")}
                  className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-emerald-500/25 font-medium flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Track Invoices
                </button>
              )}
              <button
                onClick={() => setShowCreateReceiptModal(true)}
                className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-emerald-500/25 font-medium flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Generate Payment Documents
              </button>
            </div>
          </div>
          <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            {/* Desktop View */}
            <div className="hidden md:block relative overflow-x-auto">
              <table className="table-fixed w-full border-collapse">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="pr-6 pl-20 py-4 w-[20%] text-left text-sm font-semibold text-slate-300">
                      Name
                    </th>
                    <th className="pl-2 py-4 w-[10%] text-left text-sm font-semibold text-slate-300">
                      Housing Fees
                    </th>
                    <th className="pl-10 py-2 w-[10%] text-left text-sm font-semibold text-slate-300">
                      Period
                    </th>
                    <th className="pl-16 px-1 py-2 w-[15%] text-left text-sm font-semibold text-slate-300">
                      Due Date
                    </th>
                    <th className="pl-12 px-1 py-2 w-[15%] text-left text-sm font-semibold text-slate-300">
                      Balance
                    </th>
                    <th className="pl-20 px-2 py-2 w-[20%] text-left text-sm font-semibold text-slate-300">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-800/50">
                  {processedResidents.map((resident) => (
                    <tr
                      key={resident.id}
                      className="hover:bg-slate-800/30 transition-colors cursor-pointer relative z-10"
                    >
                      {/* Name Column */}
                      <td className="pl-2 pr-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-emerald-500/10 rounded-lg">
                            <Users className="text-emerald-400 w-4 h-4" />
                          </div>
                          <span className="text-white font-medium">
                            {resident.firstName} {resident.lastName}
                          </span>
                        </div>
                      </td>

                      {/* Price Column */}
                      <td className="pl-4 py-4">
                        <input
                          type="number"
                          step="any"
                          min="0"
                          value={resident.rentAmount || ""}
                          onChange={(e) => {
                            if (checkPermission("updatePayments")) {
                              handlePriceChange(resident.id, e.target.value);
                            }
                          }}
                          onBlur={(e) => {
                            if (checkPermission("updatePayments")) {
                              // Format to 2 decimal places when leaving the field
                              const value = parseFloat(e.target.value);
                              if (!isNaN(value)) {
                                handlePriceChange(
                                  resident.id,
                                  value.toFixed(2)
                                );
                              }
                            }
                          }}
                          className={`relative z-20 bg-slate-800/50 border border-slate-700 rounded-lg px-1 py-1 text-white focus:outline-none ${
                            checkPermission("updatePayments")
                              ? "focus:ring-2 focus:ring-emerald-500/50"
                              : "cursor-not-allowed"
                          } transition-all w-20`}
                        />
                      </td>
                      {/* Period Column */}
                      <td className="pl-6 py-4">
                        <select
                          value={resident.rentSchedule}
                          onChange={(e) =>
                            checkPermission("updatePayments")
                              ? handlePeriodChange(resident.id, e.target.value)
                              : null
                          }
                          disabled={!checkPermission("updatePayments")}
                          className={`relative z-20 bg-slate-800/50 border border-slate-700 rounded-lg px-2 py-1 text-white focus:outline-none ${
                            checkPermission("updatePayments")
                              ? "focus:ring-2 focus:ring-emerald-500/50"
                              : "cursor-not-allowed"
                          } transition-all w-24`}
                        >
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </td>

                      {/* Due Date Column */}
                      <td className="pl-10 py-4">
                        <input
                          type="date"
                          defaultValue={formatDate(resident.rentDueDate)}
                          onChange={(e) =>
                            checkPermission("updatePayments")
                              ? handleDueDateChange(resident.id, e.target.value)
                              : null
                          }
                          readOnly={!checkPermission("updatePayments")}
                          className={`relative z-20 bg-slate-800/50 border border-slate-700 rounded-lg px-2 py-1 text-white focus:outline-none ${
                            checkPermission("updatePayments")
                              ? "focus:ring-2 focus:ring-emerald-500/50"
                              : "cursor-not-allowed"
                          } transition-all w-36`}
                        />
                      </td>

                      {/* Balance Column */}
                      <td className="pl-10 px-1 py-4">
                        <span
                          className={`font-medium ${
                            resident.balance >= 0
                              ? "text-emerald-400"
                              : "text-red-400"
                          } cursor-pointer`}
                          onClick={() => {
                            if (checkPermission("updatePayments")) {
                              setSelectedResident(resident);
                              setShowBalanceModal(true);
                            }
                          }}
                        >
                          ${(Number(resident.balance) || 0).toFixed(2)}
                        </span>
                      </td>

                      {/* Actions Column */}
                      <td className="px-2 py-4">
                        <div className="flex gap-2 items-center">
                          {checkPermission("addPayment") && (
                            <button
                              onClick={() => handleAddPaymentClick(resident.id)}
                              className="inline-flex items-center justify-center gap-1 w-36 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-lg transition-all duration-200"
                            >
                              <Plus size={14} />
                              Payment
                            </button>
                          )}
                          {checkPermission("viewPaymentHistory") && (
                            <button
                              onClick={() => handleViewHistory(resident.id)}
                              className="inline-flex items-center justify-center gap-1 w-36 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 px-2 py-1 rounded-lg transition-all duration-200"
                            >
                              <History size={14} />
                              History
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="md:hidden space-y-3 p-2 sm:p-4 touch-auto">
              {processedResidents.map((resident) => (
                <div
                  key={resident.id}
                  className="relative z-10 bg-slate-800/50 rounded-xl p-3 sm:p-4 border border-slate-700 hover:border-slate-600 transition-all duration-200"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="p-2 bg-emerald-500/10 rounded-lg">
                        <Users className="text-emerald-400 w-4 h-4" />
                      </div>
                      <h3 className="text-lg font-medium text-white">
                        {resident.firstName} {resident.lastName}
                      </h3>
                    </div>
                    <p
                      className={`text-lg font-bold ${
                        resident.balance >= 0
                          ? "text-emerald-400"
                          : "text-red-400"
                      }`}
                    >
                      ${(Number(resident.balance) || 0).toFixed(2)}
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Price:</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={Number(resident.rentAmount).toFixed(2)}
                      onChange={(event) =>
                        checkPermission("updatePayments")
                          ? handlePriceChange(resident.id, event.target.value)
                          : null
                      }
                      readOnly={!checkPermission("updatePayments")}
                      className={`relative z-30 bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-1.5 text-white focus:outline-none ${
                        checkPermission("updatePayments")
                          ? "focus:ring-2 focus:ring-emerald-500/50"
                          : "cursor-not-allowed"
                      } w-24 text-right`}
                    />
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Period:</span>
                    <select
                      value={resident.rentSchedule}
                      onChange={(event) =>
                        checkPermission("updatePayments")
                          ? handlePeriodChange(resident.id, event.target.value)
                          : null
                      }
                      disabled={!checkPermission("updatePayments")}
                      className={`relative z-30 bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-1.5 text-white focus:outline-none ${
                        checkPermission("updatePayments")
                          ? "focus:ring-2 focus:ring-emerald-500/50"
                          : "cursor-not-allowed"
                      } w-32`}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Due Date:</span>
                    <input
                      type="date"
                      defaultValue={formatDate(resident.rentDueDate)}
                      onChange={(event) =>
                        checkPermission("updatePayments")
                          ? handleDueDateChange(resident.id, event.target.value)
                          : null
                      }
                      readOnly={!checkPermission("updatePayments")}
                      className={`relative z-30 bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-1.5 text-white focus:outline-none ${
                        checkPermission("updatePayments")
                          ? "focus:ring-2 focus:ring-emerald-500/50"
                          : "cursor-not-allowed"
                      }`}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {checkPermission("addPayment") && (
                      <button
                        onClick={() => handleAddPaymentClick(resident.id)}
                        className="relative z-20 inline-flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-lg transition-all duration-200"
                      >
                        <Plus size={16} />
                        Payment
                      </button>
                    )}
                    {checkPermission("viewPaymentHistory") && (
                      <button
                        onClick={() => handleViewHistory(resident.id)}
                        className="relative z-30 w-full inline-flex items-center justify-center gap-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 px-4 py-2 rounded-lg transition-all duration-200"
                      >
                        <History size={16} />
                        <span className="whitespace-nowrap">History</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <PaymentModal
            isOpen={showAddPaymentModal}
            onClose={handleAddPaymentModalClose}
            paymentDetails={paymentDetails}
            handlePaymentDetailChange={handlePaymentDetailChange}
            handleSubmit={handleAddPaymentSubmit}
            fundingSourceOptions={fundingSourceOptions}
            handleDeleteFundingSource={handleDeleteFundingSource}
            generateReceipt={generateReceipt}
            setGenerateReceipt={setGenerateReceipt}
          />

          <PaymentHistoryModal
            isOpen={paymentHistoryModalOpen}
            onClose={() => setPaymentHistoryModalOpen(false)}
            paymentHistoryData={paymentHistoryData}
            onDownloadReceipt={handleDownloadReceipt}
          />

          <BalanceModal
            isOpen={showBalanceModal}
            onClose={() => setShowBalanceModal(false)}
            currentBalance={selectedResident?.balance}
            onSubmit={handleBalanceUpdate}
          />

          <PaymentDocumentsModal
            isOpen={showCreateReceiptModal}
            onClose={() => setShowCreateReceiptModal(false)}
            documentDetails={receiptDetails}
            setDocumentDetails={setReceiptDetails}
            residents={processedResidents}
            onCreateDocument={handleCreateDocument}
          />

          {/* New Funding Source Modal */}
          {isNewFundingSourceModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[200]">
              <div className="bg-slate-900 p-6 rounded-lg shadow-lg max-w-md w-full border border-slate-800">
                <h2 className="text-white text-lg font-semibold mb-4">
                  Add New Funding Source
                </h2>
                <input
                  type="text"
                  value={newFundingSource}
                  onChange={(e) => setNewFundingSource(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter new funding source"
                />
                <div className="flex justify-end gap-4 mt-4">
                  <button
                    onClick={() => {
                      setIsNewFundingSourceModalOpen(false);
                      setNewFundingSource("");
                    }}
                    className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 border border-slate-700 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleAddFundingSource(newFundingSource);
                      setIsNewFundingSourceModalOpen(false);
                      setNewFundingSource("");
                    }}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Payments;
